/**
 * Orders.
 *
 * Open orders first, because those are the ones that can still be acted on.
 * A cancel is reported as *requested*: the venue decides, and reconciliation
 * records what actually happened. Showing it as cancelled immediately would
 * mean the screen disagrees with the exchange.
 *
 * Every order shows its amount in USDT: what a buy spent or a sell brought in,
 * from its fills, fees included. An order still open shows what it holds in
 * reserve instead, since nothing has been spent yet.
 */
import { useCallback, useEffect, useState } from 'react';

import { cancelOrder, fetchOrders } from '../../api/endpoints';
import type { TradeOrder } from '../../api/types';
import { Notice } from '../../components/Notice';
import { RequirePermission } from '../../components/RequirePermission';
import { describe as describeError } from '../../lib/errors';
import { formatDateTime, formatMoney, formatPrice, formatQuantity, signOf } from '../../lib/format';

const OPEN_STATUSES = new Set(['NEW', 'PARTIALLY_FILLED']);

/** The USDT figure for one order, and what it means. */
export function orderAmount(order: TradeOrder): { value: string; label: string } | null {
  if (signOf(order.quote_amount) !== 0) {
    return { value: order.quote_amount, label: order.side === 'BUY' ? 'spent' : 'received' };
  }
  if (OPEN_STATUSES.has(order.status) && signOf(order.quote_reserved) !== 0) {
    return { value: order.quote_reserved, label: 'reserved' };
  }
  return null;
}

export function OrdersPage() {
  const [orders, setOrders] = useState<TradeOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setOrders(await fetchOrders());
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function requestCancel(order: TradeOrder) {
    try {
      await cancelOrder(order.id);
      setNotice(
        `Cancellation requested for ${order.symbol}. The venue decides; this will update once confirmed.`,
      );
      await load();
    } catch (cause) {
      setError(describeError(cause));
    }
  }

  return (
    <section className="page">
      <header className="page__header">
        <h1>Orders</h1>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}
      {notice ? <Notice tone="info">{notice}</Notice> : null}
      {loading ? <p className="muted">Loading…</p> : null}
      {!loading && orders.length === 0 ? <Notice tone="info">No orders yet.</Notice> : null}

      {orders.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>Exchange</th>
              <th>Symbol</th>
              <th>Side</th>
              <th className="num">Quantity</th>
              <th className="num">Filled</th>
              <th className="num">Price</th>
              <th className="num">Amount (USDT)</th>
              <th>Status</th>
              <th>Placed</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const amount = orderAmount(order);
              return (
              <tr key={order.id} data-testid={`order-${order.id}`}>
                <td>{order.exchange}</td>
                <td>{order.symbol}</td>
                <td className={order.side === 'BUY' ? 'gain' : 'loss'}>{order.side}</td>
                <td className="num">{formatQuantity(order.qty, 8)}</td>
                <td className="num">{formatQuantity(order.filled_qty, 8)}</td>
                <td className="num">{formatPrice(order.average_price ?? order.limit_price)}</td>
                <td className="num" data-testid={`amount-${order.id}`}>
                  {amount ? (
                    <>
                      {formatMoney(amount.value)} <span className="muted">{amount.label}</span>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{order.status}</td>
                <td>{formatDateTime(order.created_at)}</td>
                <td>
                  {OPEN_STATUSES.has(order.status) ? (
                    <RequirePermission permission="control:cancel_orders">
                      <button
                        type="button"
                        className="button button--small"
                        onClick={() => void requestCancel(order)}
                      >
                        Cancel
                      </button>
                    </RequirePermission>
                  ) : null}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
